// Google Analytics 4 & Google Tag Manager Integration

/**
 * Initialize Google Analytics 4
 * Add this to your .env file:
 * REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 * REACT_APP_GTM_ID=GTM-XXXXXXX
 */

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
export const GTM_ID = process.env.REACT_APP_GTM_ID || '';

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[Analytics] Google Analytics ID tanımlı değil. .env dosyasına REACT_APP_GA_MEASUREMENT_ID ekleyin.');
    }
    return;
  }

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    send_page_view: true,
  });
};

// Initialize Google Tag Manager
export const initGTM = () => {
  if (!GTM_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[Analytics] Google Tag Manager ID tanımlı değil. .env dosyasına REACT_APP_GTM_ID ekleyin.');
    }
    return;
  }

  // GTM script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${GTM_ID}');
  `;
  document.head.appendChild(script);

  // GTM noscript
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;
  document.body.insertBefore(noscript, document.body.firstChild);
};

// Track page view
export const trackPageView = (url) => {
  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

// E-commerce tracking
export const trackPurchase = (transactionData) => {
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionData.orderId,
      value: transactionData.total,
      currency: 'TRY',
      items: transactionData.items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
    });
  }
};

// Track add to cart
export const trackAddToCart = (product, quantity = 1) => {
  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'TRY',
      value: product.price * quantity,
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        quantity: quantity,
      }],
    });
  }
};

// Track product view
export const trackProductView = (product) => {
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'TRY',
      value: product.price,
      items: [{
        item_id: product.id,
        item_name: product.name,
        price: product.price,
        item_category: product.category,
      }],
    });
  }
};

// Track search
export const trackSearch = (searchTerm) => {
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm,
    });
  }
};

// Track contact form submission
export const trackContactForm = () => {
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      event_category: 'Contact',
      event_label: 'Contact Form Submission',
    });
  }
};


// Internal Unique Visitor Tracking
const API_URL = process.env.REACT_APP_API_URL || 'https://api.carmengold.com/api';

/** Cihaza özel kalıcı benzersiz ziyaretçi ID'si */
export const getVisitorId = () => {
  if (typeof window === 'undefined') return '';
  try {
    let vid = localStorage.getItem('carmen_visitor_id');
    if (!vid) {
      vid = 'v_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now().toString(36);
      localStorage.setItem('carmen_visitor_id', vid);
    }
    return vid;
  } catch {
    return 'v_fallback_' + Date.now().toString(36);
  }
};

let lastTrackTime = 0;

/** Backend'e tekil ziyaretçi pingi gönderir */
export const trackInternalVisitor = async () => {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  // En az 15 saniyede bir ping at (aşırı istekleri engelle)
  if (now - lastTrackTime < 15000) return;
  lastTrackTime = now;

  try {
    const visitorId = getVisitorId();
    fetch(`${API_URL}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ visitorId }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Sessiz hata
  }
};

// React Hook for Analytics
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    trackInternalVisitor();
  }, [location]);

  return {
    trackEvent,
    trackPurchase,
    trackAddToCart,
    trackProductView,
    trackSearch,
    trackContactForm,
  };
};

// Default export for backward compatibility
const GoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    trackInternalVisitor();
  }, [location]);

  return null;
};

export default GoogleAnalytics;

